import { Component, OnInit, Input, ViewChild } from '@angular/core';
import { ModalController, AlertController } from '@ionic/angular';
import { ApiService } from 'src/app/api/api.service';
import { GlobalService } from 'src/app/api/global.service';
import { MenuItem } from 'primeng/api';
import { ContextMenu } from 'primeng/contextmenu';

@Component({
  selector: 'app-groups-manager',
  templateUrl: './manager.component.html',
  styleUrls: ['./manager.component.scss'],
})
export class ManagerComponent implements OnInit {

  @Input() url: string = ''; // URL to get groups from (e.g., 'clients', 'companies', 'projects')
  @Input() selection_mode: boolean = false; // If true, allows selecting a group
  @Input() max_selection: number = 1; // Maximum number of groups that can be selected
  @Input() selected_group: any = null; // Pre-selected group if any (for single selection)
  @Input() selected_groups: any[] = []; // Pre-selected groups (for multiple selection)
  @Input() hide_group_guid: string = ''; // GUID of group to hide from list (e.g., when moving)
  @Input() modal_title: string = 'Manage Groups'; // Custom title for modal

  groups: any[] = [];
  filtered_groups: any[] = [];
  loaded: boolean = false;
  search_term: string = '';
  expanded_group_guids: Set<string> = new Set();

  group_context_items: MenuItem[] | undefined;
  @ViewChild('group_context_menu') group_context_menu: ContextMenu;
  current_context_group: any = null;

  // Group management
  editing_group: any = null;
  creating_new_group: boolean = false;
  new_group: any = {
    title: '',
    colour: '#e9ecef',
    parent_guid: null
  };

  constructor(
    private modalController: ModalController,
    private apiService: ApiService,
    public globalService: GlobalService,
    private alertController: AlertController
  ) { }

  ngOnInit() {
    this.group_context_items = [
      {
        id: 'create',
        label: 'Add Child Group',
        icon: 'fa-solid fa-plus',
        command: () => {
          this.StartCreateGroup(this.current_context_group);
        }
      },
      {
        id: 'update',
        label: 'Edit Group',
        icon: 'fa-solid fa-edit',
        command: () => {
          this.StartEditGroup(this.current_context_group);
        }
      },
      {
        id: 'update',
        label: 'Move Group',
        icon: 'fa-solid fa-arrows-up-down',
        command: () => {
          this.MoveGroup(this.current_context_group);
        }
      },
      {
        id: 'delete',
        label: 'Delete Group',
        icon: 'fa-solid fa-trash',
        command: () => {
          this.DeleteGroup(this.current_context_group, false);
        }
      },
      {
        id: 'delete',
        label: 'Force Delete Group',
        icon: 'fa-solid fa-trash-can',
        command: () => {
          this.DeleteGroup(this.current_context_group, true);
        }
      }
    ];

    this.GetGroups();
  }

  onContextMenuGroup(event: MouseEvent, group: any) {
    console.log("Group call on button");
    event.preventDefault();
    if (!this.can_manage_any) return;
    this.current_context_group = group;
    // Only the actions this team member is allowed (checked each time, since permissions load after the menu is built)
    // (item.id is the permission it needs)
    this.group_context_items = this.group_context_items.map(item => ({ ...item, visible: this.Can(item.id as 'create' | 'update' | 'delete') }));
    this.group_context_menu.show(event);
  }

  // groups_create / groups_update / groups_delete - same as the API
  Can(action: 'create' | 'update' | 'delete'): boolean {
    return this.globalService.Can('groups_' + action);
  }

  get can_manage_any(): boolean {
    return this.Can('create') || this.Can('update') || this.Can('delete');
  }

  async GetGroups() {
    this.loaded = false;
    
    // Save currently expanded groups
    this.SaveExpandedState();
    
    let data: any = await this.apiService.GetGroups(this.url);
    if (data.success) {
      this.groups = data.data || [];
      // Remove hidden group if specified
      if (this.hide_group_guid) {
        this.groups = this.groups.filter(g => g.guid !== this.hide_group_guid);
      }
      this.filtered_groups = [...this.groups];
      this.BuildGroupHierarchy();
      
      // Restore previously expanded groups
      this.RestoreExpandedState();
    }
    this.loaded = true;
  }

  BuildGroupHierarchy() {
    // Add expanded property to all groups
    this.groups.forEach(group => {
      if (group.expanded === undefined) {
        group.expanded = false;
      }
    });
    
    // If in selection mode with pre-selected groups, expand them and their parents
    if (this.selection_mode && this.selected_groups && this.selected_groups.length > 0) {
      this.ExpandSelectedGroupsAndParents();
    }
  }

  ExpandSelectedGroupsAndParents() {
    // Expand only the parent chain of selected groups, not the selected groups themselves
    this.selected_groups.forEach(selectedGroup => {
      // Find the group in the current groups array
      const group = this.groups.find(g => g.guid === selectedGroup.guid);
      if (group) {
        // Expand all parent groups (but not the group itself)
        this.ExpandParentChain(group.guid);
      }
    });
  }

  ExpandParentChain(group_guid: string) {
    const group = this.groups.find(g => g.guid === group_guid);
    if (group && group.parent_guid) {
      const parentGroup = this.groups.find(g => g.guid === group.parent_guid);
      if (parentGroup) {
        parentGroup.expanded = true;
        this.expanded_group_guids.add(parentGroup.guid);
        // Recursively expand parent's parent
        this.ExpandParentChain(parentGroup.guid);
      }
    }
  }

  SaveExpandedState() {
    this.expanded_group_guids.clear();
    this.groups.forEach(group => {
      if (group.expanded) {
        this.expanded_group_guids.add(group.guid);
      }
    });
  }

  RestoreExpandedState() {
    this.groups.forEach(group => {
      if (this.expanded_group_guids.has(group.guid)) {
        group.expanded = true;
      }
    });
  }

  SearchGroups() {
    if (!this.search_term || this.search_term.trim() === '') {
      this.filtered_groups = [...this.groups];
      // Restore normal expanded state when search is cleared
      this.RestoreExpandedState();
    } else {
      const term = this.search_term.toLowerCase();
      const matchingGroups = new Set<string>();
      
      // Find all groups that match the search term
      this.groups.forEach(group => {
        if (group.title?.toLowerCase().includes(term)) {
          matchingGroups.add(group.guid);
          // Add all parent groups
          this.AddParentGroups(group.guid, matchingGroups);
        }
      });
      
      // Filter to include matching groups and their parents
      this.filtered_groups = this.groups.filter(g => matchingGroups.has(g.guid));
      
      // Expand all groups when searching
      this.filtered_groups.forEach(group => {
        group.expanded = true;
      });
    }
    
    // Apply hide_group_guid filter
    if (this.hide_group_guid) {
      this.filtered_groups = this.filtered_groups.filter(g => 
        g.guid !== this.hide_group_guid && !this.IsDescendant(g.guid, this.hide_group_guid)
      );
    }
  }

  AddParentGroups(group_guid: string, groupSet: Set<string>) {
    const group = this.groups.find(g => g.guid === group_guid);
    if (group && group.parent_guid) {
      groupSet.add(group.parent_guid);
      this.AddParentGroups(group.parent_guid, groupSet);
    }
  }

  GetRootGroups(): any[] {
    return this.filtered_groups.filter(g => !g.parent_guid);
  }

  GetChildGroups(parent_guid: string): any[] {
    return this.filtered_groups.filter(g => g.parent_guid === parent_guid);
  }

  ToggleExpand(group: any) {
    group.expanded = !group.expanded;
    
    // Update expanded state tracking
    if (group.expanded) {
      this.expanded_group_guids.add(group.guid);
    } else {
      this.expanded_group_guids.delete(group.guid);
    }
  }

  // Selection
  SelectGroup(group: any) {
    if (!this.selection_mode) return;
    
    // For single selection (moving groups)
    if (this.max_selection === 1) {
      this.selected_group = group;
      return;
    }
    
    // For multiple selection
    if (!this.selected_groups) {
      this.selected_groups = [];
    }
    
    const index = this.selected_groups.findIndex(g => g.guid === group.guid);
    if (index > -1) {
      // Deselect
      this.selected_groups.splice(index, 1);
    } else {
      // Select
      this.selected_groups.push(group);
    }
  }
  
  IsGroupSelected(group: any): boolean {
    if (this.max_selection === 1) {
      return this.selected_group && this.selected_group.guid === group.guid;
    }
    return this.selected_groups && this.selected_groups.some(g => g.guid === group.guid);
  }

  ConfirmSelection() {
    if (this.max_selection === 1) {
      // Single selection (for moving groups)
      this.modalController.dismiss({
        selected: this.selected_group
      });
    } else {
      // Multiple selection
      this.modalController.dismiss(this.selected_groups);
    }
  }

  // Create
  StartCreateGroup(parent_group: any = null) {
    this.creating_new_group = true;
    this.new_group = {
      title: '',
      colour: '#e9ecef',
      parent_guid: parent_group ? parent_group.guid : null
    };
    
    // Auto-expand parent group if creating child
    if (parent_group) {
      parent_group.expanded = true;
      this.expanded_group_guids.add(parent_group.guid);
    }
  }

  CancelCreate() {
    this.creating_new_group = false;
    this.new_group = {
      title: '',
      colour: '#e9ecef',
      parent_guid: null
    };
  }

  async CreateGroup() {
    if (!this.new_group.title || this.new_group.title.trim() === '') {
      this.globalService.presentToastMessage('Please enter a group title', false);
      return;
    }

    let data: any = await this.apiService.post(`groups/${this.url}`, this.new_group);
    this.globalService.presentToastMessage(data.message, data.success);
    
    if (data.success) {
      this.CancelCreate();
      await this.GetGroups();
    }
  }

  // Edit
  StartEditGroup(group: any) {
    this.editing_group = { ...group };
    
    // Keep reference to original group for matching in template
    this.editing_group.guid = group.guid;
  }

  CancelEdit() {
    this.editing_group = null;
  }

  async SaveGroup() {
    if (!this.editing_group.title || this.editing_group.title.trim() === '') {
      this.globalService.presentToastMessage('Please enter a group title', false);
      return;
    }

    let data: any = await this.apiService.put(`groups/${this.url}/${this.editing_group.guid}`, this.editing_group);
    this.globalService.presentToastMessage(data.message, data.success);
    
    if (data.success) {
      this.CancelEdit();
      await this.GetGroups();
    }
  }

  // Move
  async MoveGroup(group: any) {
    const modal = await this.modalController.create({
      component: ManagerComponent,
      cssClass: 'medium-modal tall-modal secondary-modal',
      componentProps: {
        url: this.url,
        selection_mode: true,
        selected_group: null,
        hide_group_guid: group.guid,
        modal_title: `Moving Group "${group.title}"`
      }
    });

    await modal.present();
    const { data } = await modal.onDidDismiss();

    if (data && data.selected !== undefined) {
      // Allow null for moving to root
      const new_parent_guid = data.selected ? data.selected.guid : null;
      
      // Validate not moving to itself or a descendant
      if (new_parent_guid === group.guid) {
        this.globalService.presentToastMessage('Cannot move a group to itself', false);
        return;
      }
      
      if (new_parent_guid && this.IsDescendant(new_parent_guid, group.guid)) {
        this.globalService.presentToastMessage('Cannot move a group to one of its descendants', false);
        return;
      }

      await this.PerformMove(group, new_parent_guid);
    }
  }

  async PerformMove(group: any, new_parent_guid: string | null) {
    const updated_group = {
      ...group,
      parent_guid: new_parent_guid
    };

    let data: any = await this.apiService.put(`groups/${this.url}/${group.guid}`, updated_group);
    this.globalService.presentToastMessage(data.message, data.success);
    
    if (data.success) {
      await this.GetGroups();
    }
  }

  IsDescendant(potential_descendant_guid: string, ancestor_guid: string): boolean {
    const potential_descendant = this.groups.find(g => g.guid === potential_descendant_guid);
    if (!potential_descendant || !potential_descendant.parent_guid) return false;
    
    if (potential_descendant.parent_guid === ancestor_guid) return true;
    
    return this.IsDescendant(potential_descendant.parent_guid, ancestor_guid);
  }

  // Delete
  async DeleteGroup(group: any, forceDelete: boolean = false) {
    const children = this.GetChildGroups(group.guid);
    
    let header = forceDelete ? 'Force Delete Group' : 'Delete Group';
    let message = `Are you sure you want to delete "${group.title}"?`;
    
    if (forceDelete) {
      message += '\n\nForce delete will remove the group even if it contains items (contacts, companies, projects, etc.).';
      message += '\n\nType "Delete" to confirm:';
    }
    
    if (children.length > 0) {
      message += `\n\nThis group has ${children.length} child group(s) that will also be deleted.`;
    }

    const alertConfig: any = {
      header: header,
      message: message,
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel'
        },
        {
          text: 'Delete',
          role: 'destructive',
          handler: async (data: any) => {
            if (forceDelete) {
              // Check if user typed "Delete"
              if (data.confirmText !== 'Delete') {
                this.globalService.presentToastMessage('You must type "Delete" to confirm', false);
                return false;
              }
            }
            await this.PerformDelete(group, forceDelete);
          }
        }
      ]
    };

    // Add input field for force delete
    if (forceDelete) {
      alertConfig.inputs = [
        {
          name: 'confirmText',
          type: 'text',
          placeholder: 'Type "Delete" here'
        }
      ];
    }

    const alert = await this.alertController.create(alertConfig);
    await alert.present();
  }

  async PerformDelete(group: any, forceDelete: boolean = false) {
    // Delete parent group first
    let data: any = await this.apiService.delete(`groups/${this.url}/${group.guid}/${forceDelete}`, null);
    this.globalService.presentToastMessage(data.message, data.success);
    
    if (data.success) {
      // If parent deleted successfully, delete all child groups
      const children = this.GetChildGroups(group.guid);
      for (const child of children) {
        await this.PerformDelete(child, forceDelete);
      }
      
      // Refresh groups after all deletions
      await this.GetGroups();
    }
  }

  // Close modal
  Close() {
    this.modalController.dismiss();
  }
}
